import { useCallback, useMemo, useState } from "react";

import { DEFAULT_NO_EMAIL_BASE } from "@covertree/consts";
import { useUpdatePolicyholderDetailsMutation } from "@covertree/new-business-graphql";
import { Toast } from "@covertree/utils";
import { useSendBulkEsignRemindersMutation } from "@covertree/agent-portal-graphql";

import { EditContactForm } from "./form";

export function useUpdatePolicyholder(
  policyholderLocator: string,
  defaultValues: EditContactForm,
  onClose: () => void
) {
  const [invalidEmail, setInvalidEmail] = useState<string>();
  const [openBulkEsignRemindersModal, setBulkEsignRemindersModalOpen] =
    useState<boolean>(false);

  const isEligibleForEsign = useMemo(
    () => !defaultValues?.noEmail,
    [defaultValues]
  );

  const [updatePolicyholder, { loading: isUpdatePolicyholderLoading }] =
    useUpdatePolicyholderDetailsMutation();

  const [
    sendBulkEsignReminders,
    { loading: isBulkEsignRemindersLoading, data: bulkEsignRemindersResponse },
  ] = useSendBulkEsignRemindersMutation();

  const closeEmailValidationModal = useCallback(
    () => setInvalidEmail(undefined),
    []
  );
  const closeBulkEsignRemindersModal = useCallback(() => {
    setBulkEsignRemindersModalOpen(false);
    onClose();
  }, []);

  const isLoading = useMemo(
    () => isUpdatePolicyholderLoading || isBulkEsignRemindersLoading,
    [isUpdatePolicyholderLoading, isBulkEsignRemindersLoading]
  );

  const handleSubmit = useCallback(
    async ({
      emailAddress,
      primaryContactNumber,
      secondaryContactNumber,
    }: EditContactForm) => {
      const updateData = {
        emailAddress:
          emailAddress ||
          `${DEFAULT_NO_EMAIL_BASE}${primaryContactNumber}@covertree.com`,
        primaryContactNumber,
        secondaryContactNumber: secondaryContactNumber || null,
      };

      // update policyholder
      try {
        await updatePolicyholder({
          variables: {
            locator: policyholderLocator,
            input: {
              ...updateData,
            },
          },
        });
      } catch (e) {
        onClose();
        Toast.showError(
          "Could not update contact information. Please try again later."
        );
        return;
      }

      // send bulk esign reminders
      if (isEligibleForEsign && defaultValues?.emailAddress !== emailAddress) {
        try {
          await sendBulkEsignReminders({
            variables: {
              policyholderLocator,
            },
          });
          setBulkEsignRemindersModalOpen(true);
        } catch (e) {
          Toast.showError(
            "Could not send bulk esign reminders. Please try again later."
          );
        }
      } else {
        onClose();
      }
    },
    [defaultValues?.emailAddress, policyholderLocator]
  );

  return {
    loading: isLoading,
    invalidEmail,
    bulkEsignRemindersStatuses:
      bulkEsignRemindersResponse?.sendBulkEsignReminders,
    openBulkEsignRemindersModal,
    closeEmailValidationModal,
    closeBulkEsignRemindersModal,
    handleSubmit,
  };
}
